import React, { useCallback } from 'react'

import { TtFlavorContext } from '@23/ui-react'

import { setEditingMix } from '../../../firebase/hooks/useMixes'
import { useOverlayState, setChyron } from '../../../firebase/hooks/useOverlays'
import { useRole } from '../../../global/blocs/roomInfo'
import { useSpeakersQuery } from '../../../sharedLegacy/hooks/useSpeakersQuery'
import usePlaybackIntent from '../../../Webinar/hooks/usePlaybackIntent'
import useParticipantIntroOverlay from '../../../Webinar/hooks/useParticipantIntroOverlay'
import { CustomMixes } from '../CustomMixes/CustomMixes'
import { PanelMonitor } from '../PanelMonitor/PanelMonitor'
import Participant from '../Participant/Participant'
import { ChyronType } from '../../../firebase/model/chyron-model'
import {
  useFloorState,
  switchToSolo,
  switchToSideBySide,
} from '../../../firebase/hooks/useFloor'
import { useHumanParticipants } from '../../../webrtc/webRTCRoom'

import './ParticipantStrip.scss'

const ParticipantStrip = () => {
  const { data: speakers = [] } = useSpeakersQuery()
  const participants = useHumanParticipants()
  const role = useRole()
  const isProducer = role === 'producer'
  const { setPlaybackPauseIntent } = usePlaybackIntent()
  const { setParticipantIntroOverlay } = useParticipantIntroOverlay()
  const overlay = useOverlayState()

  const floor = useFloorState()

  const clearOtherSpeakerChyron = useCallback(
    (participantId) => {
      if (overlay?.type === ChyronType.Speaker) {
        const speakerFound = speakers.find(
          (speaker) => speaker.id === participantId
        )
        if (speakerFound) {
          const { name } = speakerFound
          if (name && name !== overlay.name) {
            void setChyron(null)
          }
        }
      }
    },
    [overlay, speakers]
  )

  const handleSoloClick = useCallback(
    (participantId, identifier) => {
      if (!isProducer) return

      clearOtherSpeakerChyron(participantId)

      void switchToSolo(participantId, identifier)

      if (identifier === 'camera') {
        setParticipantIntroOverlay(participantId)
      }

      void setPlaybackPauseIntent()
    },
    [
      isProducer,
      setPlaybackPauseIntent,
      setParticipantIntroOverlay,
      clearOtherSpeakerChyron,
    ]
  )

  const handleMixedModeClick = useCallback(
    (participantId) => {
      if (!isProducer) return

      clearOtherSpeakerChyron(participantId)

      void switchToSideBySide(participantId, 'screenshare')

      void setPlaybackPauseIntent()
    },
    [isProducer, setPlaybackPauseIntent, clearOtherSpeakerChyron]
  )

  return (
    <TtFlavorContext flavor="webinars">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className="participant-strip"
        onClick={(event) => {
          /* TODO: temporary. This is not a final solution */
          /* event bubbles from other interactive elements */
          if (event.target === event.currentTarget) return
          setEditingMix(null)
        }}
      >
        {participants.map((participant) => (
          <Participant
            participantId={participant.id}
            key={participant.id}
            onSoloClick={handleSoloClick}
            onMixedModeClick={handleMixedModeClick}
          />
        ))}
        {isProducer && (
          <>
            <div className="panel-monitor-enclosure">
              <PanelMonitor isSelected={floor?.layout === 'panel'} />
            </div>

            <CustomMixes />
          </>
        )}
      </div>
    </TtFlavorContext>
  )
}

export { ParticipantStrip }
